// ── DASH.B1 (#823) — role-driven Dashboard layout configuration ─────────────
//
// Single source of truth for the three-zone Dashboard's per-role weighting,
// per the design doc §5.5 (zone weighting), §5.2 (Zone 1 tiles by role) and
// §10.2 (roleConfig shape). The page reads the active role, looks up
// `DASHBOARD_ROLE_CONFIG[role]`, and renders — NO role conditionals scattered
// through the zone components (§10.2).
//
// All FIVE variants are defined and unit-tested. The live page can only RESOLVE
// three of them today (see resolveDashboardRole + the FOLD note) — 'platform_admin'
// and 'developer' are built and tested but unreachable until a role-signal
// endpoint lands. That divergence is deliberate and operator-confirmed.

import type { MyTenant } from '../hooks/useMyTenant';

export type DashboardRole =
  | 'platform_admin'
  | 'tenant_admin' // Tenant Admin / PM — the "standard" variant + the FOLD target
  | 'developer'
  | 'partner'
  | 'client';

// Zone 1 KPI tile identifiers, grouped by the role that surfaces them (§5.2).
// 'pending_approvals' and the AI-cost / coming-soon tiles are intentionally
// shared across roles where the design doc lists the same tile.
export type KpiTileId =
  // Platform Admin
  | 'active_tenants'
  | 'active_projects_all'
  | 'red_projects_platform'
  | 'ai_cost_mtd_platform'
  | 'open_critical_alerts'
  // Tenant Admin / PM
  | 'active_projects_by_phase'
  | 'sprint_progress'
  | 'open_risks'
  | 'ai_cost_mtd_tenant'
  | 'phase_gates_week'
  // Developer
  | 'current_sprint_day'
  | 'my_stories'
  | 'my_late_stories'
  | 'my_open_agent_sessions'
  // Partner sponsor
  | 'tenants_under_partner'
  | 'stale_artefacts'
  // Client sponsor
  | 'active_project'
  | 'last_activity'
  // Shared (partner + client)
  | 'pending_approvals';

// Zone 2 Action Hub card identifiers (§5.3 — universal set, role-filtered).
export type Zone2CardId =
  | 'PendingApprovals'
  | 'MyAssignedWork'
  | 'StuckItems'
  | 'NotificationsPreview'
  | 'QuickLaunch';

export interface GridTemplate {
  /** CSS `grid-template-areas` value. */
  areas: string;
  /** CSS `grid-template-columns` value. */
  columns: string;
}

export interface DashboardRoleConfigEntry {
  gridTemplate: {
    desktop: GridTemplate; // ≥ 1280px
    mobile: GridTemplate; //  < 1280px — Zone 3 stacks below Zone 2 (AC)
  };
  zone1: { tiles: KpiTileId[] };
  zone2: { cards: Zone2CardId[] };
  zone3: { visible: boolean; density: 'compact' | 'standard' };
}

// ── Shared building blocks ──────────────────────────────────────────────────

// §5.3 standard card set. Cards collapse individually when empty (§11.4), so
// listing all five is safe — only the ones with pending items actually render.
const STANDARD_CARDS: Zone2CardId[] = [
  'PendingApprovals',
  'MyAssignedWork',
  'StuckItems',
  'NotificationsPreview',
  'QuickLaunch',
];

// §5.5 "approvals-only" — client sponsors see just the approvals card in Zone 2.
const APPROVALS_ONLY: Zone2CardId[] = ['PendingApprovals'];

// Partner-context Zone 2 (#1322 + #1324): only CLIENT-scoped cards. Gates
// (PendingApprovals) and StuckItems both derive from active_projects /
// upcoming_gates, which the server scopes to the client tenant; QuickLaunch is
// static. NotificationsPreview and MyAssignedWork are intentionally EXCLUDED:
// notification_summary is queried by userId only (NOT tenant-scoped — see
// dashboardOverview.service.ts buildNotificationSummary), so in client context
// it would surface the partner's OWN cross-client notifications on this client's
// dashboard; MyAssignedWork is the partner's own work, not the client's. A
// tenant-scoped notification source for partner context is a backend follow-on.
const PARTNER_CARDS: Zone2CardId[] = ['PendingApprovals', 'StuckItems', 'QuickLaunch'];

// Zone 1 spans the full width on top; Zone 2 (left) and Zone 3 (right) sit
// below. The column ratio encodes the §5.5 weighting (Z2 ~60% / Z3 ~40%,
// or Z2-dominant for Developer).
const twoCol = (columns: string): GridTemplate => ({
  areas: '"zone1 zone1" "zone2 zone3"',
  columns,
});

// < 1280px: single column, Zone 3 stacks below Zone 2 (AC).
const STACK_MOBILE: GridTemplate = { areas: '"zone1" "zone2" "zone3"', columns: '1fr' };
// Client sponsor has Zone 3 hidden — never allocate a track for it.
const STACK_MOBILE_NO_Z3: GridTemplate = { areas: '"zone1" "zone2"', columns: '1fr' };
const SINGLE_DESKTOP_NO_Z3: GridTemplate = { areas: '"zone1" "zone2"', columns: '1fr' };

// ── The role config map (§5.5 / §10.2) ──────────────────────────────────────

export const DASHBOARD_ROLE_CONFIG: Record<DashboardRole, DashboardRoleConfigEntry> = {
  // Z1 expanded (5 tiles) · Z2 standard · Z3 cross-tenant scope
  platform_admin: {
    gridTemplate: { desktop: twoCol('3fr 2fr'), mobile: STACK_MOBILE },
    zone1: {
      tiles: [
        'active_tenants',
        'active_projects_all',
        'red_projects_platform',
        'ai_cost_mtd_platform',
        'open_critical_alerts',
      ],
    },
    zone2: { cards: STANDARD_CARDS },
    zone3: { visible: true, density: 'standard' },
  },

  // Z1 standard (5) · Z2 standard · Z3 tenant scope  [= the FOLD target]
  tenant_admin: {
    gridTemplate: { desktop: twoCol('3fr 2fr'), mobile: STACK_MOBILE },
    zone1: {
      tiles: [
        'active_projects_by_phase',
        'sprint_progress',
        'open_risks',
        'ai_cost_mtd_tenant',
        'phase_gates_week',
      ],
    },
    zone2: { cards: STANDARD_CARDS },
    zone3: { visible: true, density: 'standard' },
  },

  // Z1 compact (4) · Z2 expanded (dominant) · Z3 compact · my-work focus
  developer: {
    gridTemplate: { desktop: twoCol('7fr 3fr'), mobile: STACK_MOBILE },
    zone1: {
      tiles: ['current_sprint_day', 'my_stories', 'my_late_stories', 'my_open_agent_sessions'],
    },
    zone2: { cards: STANDARD_CARDS },
    zone3: { visible: true, density: 'compact' },
  },

  // #1322 — broadened so a partner OVERSEEING a client surfaces the client's
  // data, not just approvals: client-scoped general-data tiles (active projects
  // / sprint / risks, all derived from the server-scoped active_projects) + the
  // client-scoped PARTNER_CARDS (gates + StuckItems). Still distinct from
  // Standard: keeps the pending_approvals tile, the (FIX-3-hidden) partner-only
  // tiles, a compact Zone 3, and a CLIENT-scoped card set (no global
  // notifications — #1324). (Was §5.5 "approvals-only" — a deliberate revision.)
  partner: {
    gridTemplate: { desktop: twoCol('3fr 2fr'), mobile: STACK_MOBILE },
    zone1: {
      tiles: [
        'active_projects_by_phase',
        'sprint_progress',
        'open_risks',
        'pending_approvals',
        // Partner-ONLY tiles with no /overview backing — STILL hidden by FIX 3
        // (tracked separately in #1310). Listed so FIX 3 keeps filtering them.
        'tenants_under_partner',
        'stale_artefacts',
      ],
    },
    zone2: { cards: PARTNER_CARDS },
    zone3: { visible: true, density: 'compact' },
  },

  // Z1 minimal (3) · Z2 approvals-only · Z3 HIDDEN
  client: {
    gridTemplate: { desktop: SINGLE_DESKTOP_NO_Z3, mobile: STACK_MOBILE_NO_Z3 },
    zone1: { tiles: ['active_project', 'pending_approvals', 'last_activity'] },
    zone2: { cards: APPROVALS_ONLY },
    zone3: { visible: false, density: 'compact' },
  },
};

export function getDashboardRoleConfig(role: DashboardRole): DashboardRoleConfigEntry {
  return DASHBOARD_ROLE_CONFIG[role];
}

// ── Role resolution (the FOLD) ──────────────────────────────────────────────
//
// Build & test all five configs, but resolve only the roles the frontend can
// reliably detect from real signals:
//   - active partner-CONTEXT (partnerContextToken) → 'partner'
//   - CLIENT tenant role                           → 'client'
//   - everything else                              → 'tenant_admin' (Standard)
//
// #1315 FIX — the partner variant is gated on ACTIVE partner-CONTEXT (a partner
// viewing a client tenant via the partner portal; signalled by
// `partnerContextToken`), NOT on `myTenant.isPartner`. `isPartner` is
// partner-ACCOUNT membership — identity, orthogonal to the active view (per
// useMyTenant's docstring). Gating on it routed partner-account Tenant Admins
// running their OWN org into the sparse approvals-only partner variant, hiding
// all their PM data. The caller (Dashboard.tsx) reads the signal via
// `isInPartnerContext()` and passes it as `opts.partnerContext`, keeping this a
// pure, testable function. partnerContext takes precedence over the CLIENT
// check: while viewing a client as a partner the scoped membership row may
// itself read CLIENT, but the partner variant is the intended view.
//
// 'platform_admin' (SUPER_ADMIN, in platform_admins) and 'developer'
// (project-level role, in project_members) have NO declarative frontend signal,
// so both fold into 'tenant_admin'. CLIENT mixed-membership caveat (a user who
// is CLIENT in one tenant AND non-CLIENT in another may still see the client
// layout) is tracked in #1309 — the robust fix is a declarative backend
// `effectiveRole`.
//
// TODO(#1309, P2): expose a declarative effective-role (+ platform/project role)
// to the web so this can return 'platform_admin'/'developer' and never
// misclassify mixed-membership CLIENTs. The configs already exist + are tested.
export function resolveDashboardRole(
  myTenant: Pick<MyTenant, 'role'> | null | undefined,
  opts: { partnerContext?: boolean } = {},
): DashboardRole {
  if (opts.partnerContext) return 'partner';
  if (!myTenant) return 'tenant_admin';
  if (myTenant.role === 'CLIENT') return 'client';
  return 'tenant_admin';
}
